import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import {
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  Position,
  useReactFlow,
  useViewport,
  ViewportPortal,
  type Edge,
} from "@xyflow/react";
import { Trash2, Type } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import {
  CanvasFloatingToolbarFrame,
  canvasToolbarButtonVariants,
  CanvasToolbarDivider,
} from "../components/toolbar";
import { CANVAS_FLOATING_TOOLBAR_Z_INDEX } from "../constants/canvasFloatingToolbar.constants";
import { useCanvasUi } from "../context/CanvasUiContext";
import { readEdgeStyle } from "../utils/applyEdgeStyle";
import { getFloatingEdgeParams } from "../utils/floatingEdge";
import {
  CURVE_TYPE_ICONS,
  type ConnectorCurveType,
} from "./ConnectorFloatingToolbar";
import { ArrowEndPicker, type ArrowEndStyle } from "./shared/ArrowEndPicker";
import { EDGE_COLORS } from "./shared/CanvasColorPalettes";
import { CanvasColorPickerButton } from "./shared/CanvasColorPickerButton";
import { LineStylePicker, type LineStyle } from "./shared/LineStylePicker";

const CURVE_OPTIONS: { value: ConnectorCurveType; label: string }[] = [
  { value: "straight", label: "Straight" },
  { value: "smooth", label: "Smooth" },
  { value: "step", label: "Step" },
];

export interface EdgeStyle {
  color?: string;
  stroke_style?: "solid" | "dashed" | "dotted";
  stroke_width?: number;
  start_arrow?: ArrowEndStyle;
  end_arrow?: ArrowEndStyle;
  curve_type?: ConnectorCurveType;
  /** @deprecated use startArrow/endArrow instead */
  arrow_mode?: "none" | "end" | "both";
  [key: string]: unknown;
}

interface EdgeFloatingToolbarProps {
  edge: Edge;
  onStyleChange: (edgeId: string, style: EdgeStyle) => void;
  onDelete: (edgeId: string) => void;
  onPopoverOpenChange?: (open: boolean) => void;
}

/**
 * Floating toolbar for selected edges.
 * Uses ViewportPortal so it stays in flow-space and scales with zoom.
 */
export function EdgeFloatingToolbar({
  edge,
  onStyleChange,
  onDelete,
  onPopoverOpenChange,
}: EdgeFloatingToolbarProps) {
  const { getInternalNode } = useReactFlow();
  const viewport = useViewport();
  const { editingEdgeId, setEditingEdgeId } = useCanvasUi();
  const [isCurveOpen, setIsCurveOpen] = useState(false);

  const isColorOpenRef = useRef(false);
  const isLineStyleOpenRef = useRef(false);
  const isStartOpenRef = useRef(false);
  const isEndOpenRef = useRef(false);
  const isCurveOpenRef = useRef(false);

  const notifyOpen = useCallback(() => {
    onPopoverOpenChange?.(
      isColorOpenRef.current ||
        isLineStyleOpenRef.current ||
        isStartOpenRef.current ||
        isEndOpenRef.current ||
        isCurveOpenRef.current,
    );
  }, [onPopoverOpenChange]);

  const handleColorOpen = useCallback(
    (open: boolean) => {
      isColorOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleLineStyleOpen = useCallback(
    (open: boolean) => {
      isLineStyleOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleStartOpen = useCallback(
    (open: boolean) => {
      isStartOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleEndOpen = useCallback(
    (open: boolean) => {
      isEndOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  const handleCurveOpen = useCallback(
    (open: boolean) => {
      setIsCurveOpen(open);
      isCurveOpenRef.current = open;
      notifyOpen();
    },
    [notifyOpen],
  );

  // Hide the style toolbar while this edge's label is being edited — the inline
  // label editor owns the space then (avoids overlapping it).
  if (editingEdgeId === edge.id) return null;

  const sourceNode = getInternalNode(edge.source);
  const targetNode = getInternalNode(edge.target);
  if (!sourceNode || !targetNode) return null;

  // Read current state from the edge's native fields (style/markers + curve in
  // data) — the source of truth for both agent- and client-created edges.
  const edgeStyle = readEdgeStyle(edge);
  const currentColor = edgeStyle.color ?? "default";
  const currentStrokeStyle = edgeStyle.stroke_style ?? "solid";
  const currentStrokeWidth = edgeStyle.stroke_width ?? 2;
  const currentStartArrow: ArrowEndStyle = edgeStyle.start_arrow ?? "none";
  const currentEndArrow: ArrowEndStyle = edgeStyle.end_arrow ?? "arrow";
  const currentCurve: ConnectorCurveType = edgeStyle.curve_type ?? "step";

  // Position the toolbar above the edge's label by computing the *same* label
  // midpoint the edge itself does (see LabeledEdge): derive each end's anchor +
  // side from getFloatingEdgeParams — the floating routing the edge now renders
  // with — then run the matching path helper for the curve. We MUST use the
  // same source as LabeledEdge (floating dominant-axis side midpoints), not the
  // stored edge.sourceHandle/targetHandle, otherwise the toolbar drifts away
  // from the label once nodes move and the floating routing picks new sides.
  // resolveHandle remains only as a fallback for the brief pre-measure window
  // where getFloatingEdgeParams returns null.
  // A node-center midpoint only lines up for bezier — straight uses the
  // endpoints (→ horizontal offset) and step routes orthogonally
  // (→ large vertical offset).
  const resolveHandle = (
    node: NonNullable<ReturnType<typeof getInternalNode>>,
    handleId: string | null | undefined,
  ) => {
    const handles = [
      ...(node.internals.handleBounds?.source ?? []),
      ...(node.internals.handleBounds?.target ?? []),
    ];
    const handle =
      (handleId ? handles.find((h) => h.id === handleId) : undefined) ??
      handles[0];
    const ax = node.internals.positionAbsolute?.x ?? 0;
    const ay = node.internals.positionAbsolute?.y ?? 0;
    if (!handle) {
      return {
        x: ax + (node.measured?.width ?? 0) / 2,
        y: ay + (node.measured?.height ?? 0) / 2,
        position: Position.Top,
      };
    }
    return {
      x: ax + handle.x + handle.width / 2,
      y: ay + handle.y + handle.height / 2,
      position: handle.position,
    };
  };

  // Mirror LabeledEdge exactly: prefer the floating params, fall back to the
  // stored-handle resolution only when nodes aren't measured yet.
  const floating = getFloatingEdgeParams(sourceNode, targetNode);
  const s = floating
    ? { x: floating.sx, y: floating.sy, position: floating.sourcePos }
    : resolveHandle(sourceNode, edge.sourceHandle);
  const t = floating
    ? { x: floating.tx, y: floating.ty, position: floating.targetPos }
    : resolveHandle(targetNode, edge.targetHandle);
  const [, labelX, labelY] =
    currentCurve === "straight"
      ? getStraightPath({
          sourceX: s.x,
          sourceY: s.y,
          targetX: t.x,
          targetY: t.y,
        })
      : currentCurve === "step"
        ? getSmoothStepPath({
            sourceX: s.x,
            sourceY: s.y,
            sourcePosition: s.position,
            targetX: t.x,
            targetY: t.y,
            targetPosition: t.position,
          })
        : getBezierPath({
            sourceX: s.x,
            sourceY: s.y,
            sourcePosition: s.position,
            targetX: t.x,
            targetY: t.y,
            targetPosition: t.position,
          });

  const hasLabel = typeof edge.label === "string" && edge.label.length > 0;
  // Fixed distance above the label midpoint — the SAME whether or not a label
  // is set. The toolbar is ~56px tall and grows downward from this anchor, so
  // the offset must exceed that height or its bottom dips onto the line; 76 also
  // clears a label sitting on the midpoint.
  const TOOLBAR_OFFSET = 76;
  const flowY = labelY - TOOLBAR_OFFSET / viewport.zoom;

  return (
    <ViewportPortal>
      <div
        className="canvas-floating-toolbar-portal"
        style={{
          position: "absolute",
          left: labelX,
          top: flowY,
          transform: `translateX(-50%) scale(${1 / viewport.zoom})`,
          transformOrigin: "top center",
          pointerEvents: "auto",
          zIndex: CANVAS_FLOATING_TOOLBAR_Z_INDEX,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="h-2 w-full" />
        <TooltipProvider delayDuration={300}>
          <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
            {/* Color */}
            <CanvasColorPickerButton
              colors={EDGE_COLORS}
              activeValue={currentColor}
              tooltip="Edge color"
              onColorChange={(color) => onStyleChange(edge.id, { color })}
              onOpenChange={handleColorOpen}
            />

            {/* Line style (style + width combined) */}
            <LineStylePicker
              value={{
                strokeStyle: currentStrokeStyle,
                strokeWidth: currentStrokeWidth,
              }}
              onChange={(ls: LineStyle) =>
                onStyleChange(edge.id, {
                  stroke_style: ls.strokeStyle,
                  stroke_width: ls.strokeWidth,
                })
              }
              onOpenChange={handleLineStyleOpen}
            />

            <CanvasToolbarDivider />

            {/* Start arrow */}
            <ArrowEndPicker
              value={currentStartArrow}
              onChange={(v) => onStyleChange(edge.id, { start_arrow: v })}
              onOpenChange={handleStartOpen}
              label="Start"
              isStart
            />

            {/* Curve type */}
            <Popover open={isCurveOpen} onOpenChange={handleCurveOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <PopoverTrigger asChild>
                    <button
                      className={canvasToolbarButtonVariants()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {CURVE_TYPE_ICONS[currentCurve]}
                    </button>
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">Curve type</TooltipContent>
              </Tooltip>
              <PopoverContent
                className="w-40 p-1 rounded-xl"
                align="center"
                side="top"
                sideOffset={14}
                onClick={(e) => e.stopPropagation()}
              >
                {CURVE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      onStyleChange(edge.id, { curve_type: opt.value });
                      handleCurveOpen(false);
                    }}
                    className={cn(
                      "w-full px-3 py-1.5 flex items-center gap-2.5 rounded-lg transition-colors",
                      "hover:bg-n-95/8 dark:hover:bg-n-95/10",
                      currentCurve === opt.value &&
                        "bg-primary/10 text-primary font-medium",
                    )}
                  >
                    {CURVE_TYPE_ICONS[opt.value]}
                    <span className="text-xs">{opt.label}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>

            {/* End arrow */}
            <ArrowEndPicker
              value={currentEndArrow}
              onChange={(v) => onStyleChange(edge.id, { end_arrow: v })}
              onOpenChange={handleEndOpen}
              label="End"
            />

            {/* Add / edit label — the discoverable entry point (the label
                text is only clickable once one exists). Sets editingEdgeId,
                which hides this toolbar and opens the inline editor. */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingEdgeId(edge.id);
                  }}
                  className={canvasToolbarButtonVariants()}
                >
                  <Type className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {hasLabel ? "Edit text" : "Add text"}
              </TooltipContent>
            </Tooltip>

            <CanvasToolbarDivider />

            {/* Delete */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(edge.id);
                  }}
                  className={canvasToolbarButtonVariants({ destructive: true })}
                >
                  <Trash2 className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">Delete</TooltipContent>
            </Tooltip>
          </CanvasFloatingToolbarFrame>
        </TooltipProvider>
        <div className="h-2 w-full" />
      </div>
    </ViewportPortal>
  );
}
